import React from "react";
import { Alert, Box, Dialog, Stack, Text } from "@origin-digital/ods-core";
import {
  AddressPartsMap,
  BulkDuplicateDialogProps,
  BulkDuplicateFormValues,
  BulkDuplicateResponse,
} from "./types";
import { useBulkDuplicateDialogContext } from "./context";
import { BulkDuplicatePremisesSelection } from "./BulkDuplicatePremisesSelection";
import { Formik, useFormikContext } from "formik";
import { PremiseDetails, State } from "../types";
import { getFieldsDataFromAddress, getFormattedAddress } from "../helpers";
import { IconProperty } from "@origin-digital/ods-icons";
import BulkDuplicateActions from "./BulkDuplicateActions";
import BulkDuplicateAssociatedServices from "./BulkDuplicateAssociatedServices";
import BulkDuplicateAddressParts from "./BulkDuplicateAddressParts";
import { useDashboardContext } from "src/templates";
import { mapBulkDuplicateDraftRequest } from "src/api/apiHelper";
import { apiClient } from "src/api/apiService";

export const BulkDuplicateDialogContent = ({
  showAlert,
}: {
  showAlert: boolean;
}) => {
  const { closeDialog, id } = useBulkDuplicateDialogContext();
  const { values } = useFormikContext<BulkDuplicateFormValues>();
  const addressString = getFormattedAddress(values?.addressObj);

  return (
    <Dialog
      id={id}
      open={true}
      title="Bulk duplicate requests"
      onClose={() => closeDialog()}
      maxWidth="xl"
      stickyTitle
    >
      <Stack space="xlarge">
        {/* Introduction and source location */}
        <Stack space="large">
          <Text>
            This form will allow you to create up to 20 draft requests based on
            the below premises and it’s services. Uploaded files will not be
            copied over to new requests as they must be unique to each request.
          </Text>
          <Text>You&apos;re duplicating from:</Text>
          {/* Address */}
          <Box display="flex" alignItems="flex-start" gap="xsmall">
            <IconProperty color="neutral" />
            <Text weight="medium">{addressString}</Text>
          </Box>
          {/* End Address */}
        </Stack>
        {/* End introduction and source location */}

        {/* Address update selection */}
        <BulkDuplicateAddressParts />
        {/* End address update selection */}

        {/* Premises selection */}
        <BulkDuplicatePremisesSelection />
        {/* End premises selection */}

        {/* Service selection */}
        <BulkDuplicateAssociatedServices />
        {/* End service selection */}

        {showAlert && (
          <Alert tone="critical" title="Failed to create new requests">
            It’s not you, it’s us. Please try again or come back later.
          </Alert>
        )}
        {/* Action buttons */}
        <BulkDuplicateActions />
        {/* End action buttons */}
      </Stack>
    </Dialog>
  );
};

export const BulkDuplicateDialog: React.FC<BulkDuplicateDialogProps> = (
  props
) => {
  const { closeDialog } = useBulkDuplicateDialogContext();
  const { snackbarSection } = useDashboardContext();
  const [showAlert, setShowAlert] = React.useState(false);
  const { premise, services } = props;
  const state = premise?.site_details?.state as State;
  const getAddressParts = (premiseState: State): AddressPartsMap => {
    const addIfNotBlank = (
      obj: AddressPartsMap,
      key: string,
      label: string
    ) => {
      const value = getFieldsDataFromAddress(premise as PremiseDetails, key);
      if (value && value !== "") {
        obj[key] = label;
      }
    };
    const fields = {} as AddressPartsMap;
    switch (premiseState) {
      case State.ACT:
        addIfNotBlank(fields, "dp_number", "DP number");
        addIfNotBlank(fields, "unit_number", "Unit number");
        addIfNotBlank(fields, "floor_number", "Level");
        addIfNotBlank(fields, "street_number", "Street number");
        addIfNotBlank(fields, "block_number", "Block number");
        addIfNotBlank(fields, "section_number", "Section number");
        break;
      case State.NSW:
        addIfNotBlank(fields, "unit_number", "Unit number");
        addIfNotBlank(fields, "floor_number", "Level");
        addIfNotBlank(fields, "street_number", "Street number");
        addIfNotBlank(fields, "lot_number", "Lot number");
        addIfNotBlank(fields, "section_number", "Section number");
        addIfNotBlank(fields, "dp_number", "DP number");
        break;
      case State.QLD:
      case State.SA:
      case State.VIC:
        addIfNotBlank(fields, "unit_number", "Unit number");
        addIfNotBlank(fields, "floor_number", "Level");
        addIfNotBlank(fields, "street_number", "Street number");
        addIfNotBlank(fields, "lot_number", "Lot number");
        break;
      default:
        break;
    }
    return fields;
  };
  const addressParts = getAddressParts(state);
  const initialValues: BulkDuplicateFormValues = {
    addressParts,
    services,
    selectedAddressParts: [],
    selectedServices: services?.map((service) => service.service_type) || [],
    addressObj: premise as PremiseDetails,
    premises: [],
  };

  const handleCopyAddress = async (item: string) => {
    try {
      await navigator.clipboard.writeText(item);
    } catch (err) {
      console.error("Failed to copy address:", err);
    }
  };

  const handleSubmit = async (values: BulkDuplicateFormValues) => {
    try {
      setShowAlert(false);
      const response = await apiClient.post<BulkDuplicateResponse>(
        `/draft/bulk/create-premises?sourcePremiseId=${premise.id}`,
        mapBulkDuplicateDraftRequest(values)
      );
      const data = response.data;

      if (data?.drafts_failed) {
        if (data.drafts_created) {
          closeDialog();
          const failedPremiseList = data.drafts_failed.map((failedPremise) =>
            getFormattedAddress(failedPremise as PremiseDetails)
          );
          snackbarSection.show({
            children: `Failed to create ${data.drafts_failed.length}/${values.premises.length} drafts.`,
            showClose: true,
            autoHideDuration: 5000,
            tone: "caution",
            actionLabel: "Copy addresses",
            actionOnClick: () => {
              handleCopyAddress(failedPremiseList.join("; "));
            },
          });
        } else {
          setShowAlert(true);
        }
      } else {
        closeDialog();
        props.reFetchPremises();
        snackbarSection.show({
          children: "Drafts created.",
          showClose: true,
          autoHideDuration: 5000,
          tone: "positive",
        });
      }
    } catch (e) {
      console.error("Failed to create drafts:", e);
      setShowAlert(true);
    }
  };

  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmit}>
      <BulkDuplicateDialogContent showAlert={showAlert} />
    </Formik>
  );
};
